import { ChevronRight, AlertTriangle, Plus } from 'lucide-react';
import { createElement, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";

import { PictoVoirDetail } from '@/assets/icons-april-fr/pictos';
import SmallSpinner from '@/components/shared/spinners/small-spinner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { useTerminateAssuranceTransitoire } from '@/hooks/api/use-contract-actions';
import { useContracts, useContractsData } from '@/hooks/api/use-contracts';
import { useCurrentUser } from '@/hooks/api/use-user';
import { hasOnlyContractApporteur43397 } from '@/lib/contracts';
import { toDate } from '@/lib/dates';
import type { Contract } from '@/types/api/contract';
import getProductIcon from '@/lib/product-icon';

import ProductPickerModal from '@/components/shared/product-picker-modal';

/** Bloc « Mes contrats » de l'accueil. */
const Contracts = () => {
  const contractsQuery = useContracts();
  const contracts = useContractsData();
  const subscribeLinks: Record<string, string> = useCurrentUser().prefilled_subscribe_links || {};
  const souscriptionGenerale = subscribeLinks.general;
  const hasOnlyApporteur43397 = hasOnlyContractApporteur43397(contracts);
  const terminateAssuranceTransitoire = useTerminateAssuranceTransitoire();
  const navigate = useNavigate();
  const [productPickerOpen, setProductPickerOpen] = useState(false);

  const handleTerminateAssuranceTransitoire = (contractId: number) => {
    Swal.fire({
      title: 'Confirmation',
      text: "Confirmez-vous avoir vendu votre ancien véhicule ? Cela mettra fin à l'assurance transitoire.",
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Oui, je confirme',
      cancelButtonText: 'Annuler',
    }).then((result) => {
      if (result.isConfirmed) {
        terminateAssuranceTransitoire.mutate({ contractId });
      }
    });
  };

  const hasActiveTransitoire = (contract: Contract) => {
    return (
      contract.old_vehicule?.immatriculation &&
      !contract.vehicule?.vente_old_vehicule &&
      toDate(contract.old_vehicule.date_fin_piece).getTime() > Date.now()
    );
  };

  const getStatusVariant = (statutCode?: string) => {
    switch (statutCode) {
      case '1':
        return 'success';
      case '6':
      case '5':
        return 'destructive';
      case 'I':
      case '2':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const labelOverrides: Record<string, { label: ReactNode; article: string }> = {
    moto: { label: 'moto', article: 'une' },
    '50': {
      label: (
        <>
          cyclo <span aria-hidden="true">50cm3</span>
          <span className="sr-only">50 centimètres cubes</span>
        </>
      ),
      article: 'un',
    },
    scooter: { label: 'scooter', article: 'un' },
    quad: { label: 'quad', article: 'un' },
    verte: { label: 'moto verte', article: 'une' },
    trottinette: { label: 'trottinette électrique', article: 'une' },
    velo: { label: 'vélo à assistance électrique', article: 'un' },
    livraison: { label: 'véhicule de livraison', article: 'un' },
  };

  const productOptions = subscribeLinks
    ? Object.keys(subscribeLinks)
        .filter((key) => key !== 'general')
        .reduce<Record<string, ReactNode>>((acc, key) => {
          const override = labelOverrides[key];
          if (override) {
            acc[key] = (
              <>
                Je souhaite assurer {override.article} {override.label}
              </>
            );
          }
          return acc;
        }, {})
    : {};

  const handleNewQuote = () => {
    if (!subscribeLinks) return;
    setProductPickerOpen(true);
  };

  const handleProductSelect = (key: string) => {
    const link = subscribeLinks[key];
    if (link) {
      window.open(link, '_blank');
    }
  };

  // Ancien loader : spinner tant que les contrats ne sont pas chargés.
  if (contractsQuery.isPending) return <SmallSpinner />;

  return (
      <Card data-tutorial="home-contracts" className="relative min-w-0">
          <Link
              to="/mes-contrats"
              className="absolute top-6 right-6 text-sm text-muted-foreground transition-colors hover:text-foreground no-underline inline-flex items-center gap-1"
          >
              Tous les contrats
              <ChevronRight className="w-4 h-4" aria-hidden="true" />
          </Link>
          <CardHeader className="pb-2">
              <CardTitle className="text-lg">Mes contrats</CardTitle>
          </CardHeader>
          <CardContent className="px-0">
              {contracts && contracts.length > 0 ? (
                  <>
                      {/* Mobile: liste empilée */}
                      <div className="sm:hidden max-h-[220px] overflow-y-auto divide-y">
                          {contracts.map((c, i) => (
                              <div
                                  key={i}
                                  {...(i === 0 ? { 'data-tutorial': 'home-contract-row-mobile' } : {})}
                                  className="flex items-center justify-between gap-3 px-6 py-4 cursor-pointer hover:bg-muted/50 transition-colors"
                                  onClick={() => navigate(`/mes-contrats/mon-contrat/${c.id}`)}
                              >
                                  <div className="flex items-center gap-2 min-w-0">
                                      {createElement(getProductIcon(c.vehicule?.codeProduit), {
                                          className: 'w-4 h-4 text-muted-foreground shrink-0',
                                          'aria-hidden': 'true'
                                      })}
                                      <div className="min-w-0">
                                          <p className="font-medium text-sm break-words">
                                              {c.vehicule?.marque} {c.vehicule?.modele}
                                          </p>
                                          <p className="text-xs text-muted-foreground">
                                              n°{c.numeroPolice}
                                          </p>
                                      </div>
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                      <Badge variant={getStatusVariant(c.situation?.statutCode)} className="text-xs">
                                          {c.situation?.statut}
                                      </Badge>
                                      <ChevronRight className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                                  </div>
                              </div>
                          ))}
                      </div>

                      {/* Desktop: tableau de mise en forme (pas de structure tabulaire signifiante) */}
                      <div className="hidden sm:block max-h-[180px] overflow-y-auto">
                      <Table role="presentation">
                          <TableBody>
                              {contracts.map((c, i) => (
                                  <TableRow
                                      key={i}
                                      {...(i === 0 ? { 'data-tutorial': 'home-contract-row' } : {})}
                                      className="[&_td]:align-middle cursor-pointer"
                                      onClick={() => navigate(`/mes-contrats/mon-contrat/${c.id}`)}
                                  >
                                      {/* w-full + whitespace-normal : la colonne prend la place restante et le libellé passe à la ligne au lieu de faire défiler le tableau */}
                                      <TableCell className="font-medium !pl-4 w-full whitespace-normal">
                                          <div className="flex items-center gap-2">
                                              {createElement(getProductIcon(c.vehicule?.codeProduit), {
                                                  className: 'w-4 h-4 text-muted-foreground shrink-0'
                                              })}
                                              <span className="break-words">
                                                  {c.vehicule?.marque} {c.vehicule?.modele}
                                              </span>
                                          </div>
                                      </TableCell>
                                      <TableCell className="text-muted-foreground">
                                          {c.numeroPolice}
                                      </TableCell>
                                      <TableCell className="hidden md:table-cell lg:hidden xl:table-cell">
                                          {c.vehicule?.immatriculation && (
                                              <span className="font-mono text-sm bg-muted px-1.5 py-0.5 rounded-md">
                                                  {c.vehicule.immatriculation}
                                              </span>
                                          )}
                                      </TableCell>
                                      <TableCell>
                                          <Badge variant={getStatusVariant(c.situation?.statutCode)} className="text-sm">
                                              {c.situation?.statut}
                                          </Badge>
                                      </TableCell>
                                      <TableCell className="text-right !pr-4">
                                          <Link
                                              to={`/mes-contrats/mon-contrat/${c.id}`}
                                              className="inline-flex items-center justify-center w-9 h-9 rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                              aria-label={`Voir le détail du contrat ${c.numeroPolice}`}
                                          >
                                              <PictoVoirDetail className="w-4 h-4" aria-hidden="true" />
                                          </Link>
                                      </TableCell>
                                  </TableRow>
                              ))}
                          </TableBody>
                      </Table>
                      </div>

                      {/* Alerts for transitoire */}
                      {contracts.map(
                          (c, i) =>
                              hasActiveTransitoire(c) && (
                                  <div key={`alert-${i}`} className="p-6">
                                      <Alert variant="warning">
                                          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                                          <AlertDescription>
                                              <p className="text-sm font-medium mb-2">
                                                  Vous venez de changer le véhicule assuré sur votre contrat.
                                              </p>
                                              <p className="text-sm mb-4">
                                                  Avez-vous vendu votre ancien véhicule {c.old_vehicule?.marque}{' '}
                                                  {c.old_vehicule?.modele}&nbsp;?
                                              </p>
                                              <Button
                                                  size="sm"
                                                  onClick={() => handleTerminateAssuranceTransitoire(c.id)}
                                              >
                                                  OUI j'ai vendu ce véhicule
                                              </Button>
                                          </AlertDescription>
                                      </Alert>
                                  </div>
                              ),
                      )}
                  </>
              ) : (
                  <p className="text-sm text-center text-muted-foreground py-6">Aucun contrat trouvé.</p>
              )}

                        </CardContent>

          {/* Nouveau devis button - toujours en bas de la Card */}
          {souscriptionGenerale && hasOnlyApporteur43397 && (
              <div className="px-6 py-6 border-t mt-auto">
                  <Button
                      className="w-full cursor-pointer py-4"
                      onClick={handleNewQuote}
                      aria-haspopup="dialog"
                      aria-expanded={productPickerOpen}
                  >
                      <Plus className="w-4 h-4" aria-hidden="true" />
                      Assurer un nouveau véhicule
                  </Button>
              </div>
          )}

          <ProductPickerModal
            isOpen={productPickerOpen}
            onClose={() => setProductPickerOpen(false)}
            onSelect={handleProductSelect}
            options={productOptions}
          />
      </Card>
  );
};

export default Contracts;
